/** @jsxImportSource @emotion/react */
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Column, Row } from "../layout/flexbox";
import { css } from "@emotion/react";
import { getResource } from "../language/resources/getResource";
import { useDispatch, useSelector } from "react-redux";
import { IAppState } from "../core/redux/IAppState";
import { getSellingSeasonsAction } from "./getSellingSeasonsAction";
import { getResortDetailsAction } from "../resortDetails/getResortDetailsAction";
import { getFulfillmentSeasonsAction } from "./getFulfillmentSeasonsAction";
import {
  AccessNode,
  AccessOverviewQuery,
  FulfillmentSeason,
  LiftAccessRuleBatchSaveResult,
  SellingSeason,
} from "./models";
import {
  compareByNameAsc,
  extractIds,
  resortToAccessNode,
  denormalizeFilter,
  shouldIncludeResort,
  RESORT_EXCLUSIONS,
} from "./module";
import { ACTION_TYPE } from "../core/redux/actionType";
import { getError, getLoading } from "../core/redux/api/module";
import { showSnackbarAction } from "../snackbar/snackbarAction";
import { ApiError } from "../core/redux/api/apiError";
import { clearApiErrorAction } from "../core/redux/api/clearApiErrorAction";
import { useApiErrorSnackbar } from "../language/hooks/useApiErrorSnackbar";
import { searchLiftAccessRulesAction } from "./searchLiftAccessRulesAction";
import { bulkSaveLiftAccessRulesBatched } from "./bulkSaveLiftAccessRulesBatched";
import { BulkProgressCard } from "../forms/elements/BulkProgressCard";
import type { AccessRuleChangeRequest } from "./accessRules/requests/accessRuleRequestBuilder";
import { LiftAccessRuleSetTable } from "./accessRules/liftAccessRuleSet/LiftAccessRuleSetTable";
import { TablePagination } from "@mui/material";
import { sortByProp } from "../language/module";
import { AccessOverviewFilters } from "./AccessOverviewFilter";
import { sortArrayOfObjectsProperty } from "../language/module";
import ErrorView from "../forms/ErrorView";
import {
  BoxedErrorInverse,
  BoxedErrorInverseNoBorder,
} from "../forms/elements/boxedMessage/boxedMessageStyles";
import React from "react";
import {
  LiftAccessRuleSet,
  liftAccessRuleSetKey,
} from "./accessRules/liftAccessRuleSet/models";
import {
  BulkFailureIdentity,
  BulkSaveFailuresView,
} from "./accessRules/liftAccessRuleSet/BulkSaveFailuresView";
import {
  BulkSaveRun,
  failureCount,
  mergeSaveOutcomes,
  notAttemptedCount,
  retryableSaveRequests,
  successCount,
  toSaveFailures,
} from "./accessRules/liftAccessRuleSet/bulkSaveRun";
import {
  BulkDeleteRun,
  deleteFailureCount,
  deleteSuccessCount,
  notAttemptedRuleSets,
  retryableDeleteRuleSets,
  toDeleteFailures,
} from "./accessRules/liftAccessRuleSet/bulkDeleteRun";
import { SelectAllToolbar } from "./multiSelect/SelectAllToolbar";
import { useRowRangeSelection } from "./multiSelect/useRowRangeSelection";
import { showModalAction } from "../core/modal/showModalAction";
import { IKON_MODAL } from "../core/modal/ikonModal";
import * as Buttons from "../forms/elements/buttons";
import { ItemKey } from "./multiSelect/types";
import { ENTITLEMENT_CATEGORY_ID } from "../guest/entitlements/constants";
import ConfirmationModal from "../forms/elements/ConfirmationModal";
import deleteIcon from "../assets/icons/trashcan.svg";
import {
  DARK_RED_CREEK,
  LIGHT_GREY,
  RED_CREEK,
} from "../styles/colors";
import failureIcon from "../assets/icons/failureTriangleRed.svg";
import { LiftAccessRuleSetHeader } from "./accessRules/liftAccessRuleSet/LiftAccessRuleSetHeader";
import { buildSingleRuleSetDeleteRequest } from "./accessRules/requests/singleRuleSetDeleteRequest";
import { bulkDeleteLiftAccessRuleSetsAction } from "./bulkDeleteLiftAccessRuleSetsAction";
import { bulkDeleteLiftAccessRulesChunked } from "./bulkDeleteLiftAccessRulesChunked";
import { useSearchResultFreshness } from "./useSearchResultFreshness";
import { AccessMultiDeleteModal } from "./AccessMultiDeleteModal";
import { useApiActionHandler } from "../language/hooks/useApiActionHandler";
import { usePageLockedByPopout } from "../layout/window/popoutLock";
import { POPOUT_NOTICE_SLOT_ID } from "../layout/window/PopoutBlocker";

// Matches LiftAccessSearchResultLimit, so anything selectable is deletable. The run is
// chunked regardless; this only bounds the total length of one run, and is checked
// before the first chunk is sent rather than per chunk.
const MAX_BULK_DELETE_SELECTION = 5000;

const overridemuifont = css`
  .MuiTablePagination-selectLabel {
    font-size: 1rem;
  }
  .MuiTablePagination-root {
    font-size: 1rem;
  }
  .MuiTablePagination-displayedRows {
    font-size: 1rem;
  }
  .MuiTablePagination-actions {
    font-size: 1rem;
  }
`;

const tablePagination = css`
  margin-left: auto;
  margin-right: 50px;
  ${overridemuifont}
`;

const accessOverviewRoot = css`
  margin: 20px 15px;
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  height: 100%;
  overflow-x: visible;
  overflow-y: hidden;
  display: flex;
  flex-direction: column;
`;

const tableSection = css`
  margin-top: 15px;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  width: fit-content;
`;

const tableContainer = css`
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  max-width: 100%;
  overflow-x: visible;
  overflow-y: auto;
  scrollbar-gutter: auto;
  touch-action: pan-y;
  -webkit-overflow-scrolling: touch;
`;

const tableRoot = css`
  margin: 0;
  width: 100% !important; /* Allow table to be as wide as its content */
  min-width: 100% !important;
  box-sizing: border-box;

  table {
    /** Ensure table takes full width of container and columns respect specified widths */
    table-layout: fixed !important;
  }
`;

const addButton = css`
  height: 50px;
  padding: 0 20px; /* Adjust horizontally as needed */
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
`;

const selectionRow = css`
  margin-left: 25px;
  margin-top: 8px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
`;

const bulkActions = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
`;

// Clicking re-searches, keeping the selection. SMALL_BUTTON_STYLE so it matches the
// height of the buttons it sits between.
const staleResultsNoticeCss = css`
  ${Buttons.SMALL_BUTTON_STYLE};
  display: inline-flex;
  align-items: center;
  gap: 8px;
  box-sizing: border-box;
  color: ${RED_CREEK};
  background: transparent;
  border: solid 1px ${RED_CREEK};

  &:hover:not(:disabled) {
    color: ${DARK_RED_CREEK};
    border-color: ${DARK_RED_CREEK};
  }

  img {
    width: 15px;
    height: 15px;
    flex: 0 0 auto;
  }
`;

// Pairs with `inert`, which is what actually takes a region out of tab order and
// hides it from assistive tech. This only says why.
const lockedRegion = css`
  opacity: 0.45;
`;

// Shares the filter row, so the notice costs no vertical space and covers no
// results — that row is locked anyway and has room beside Add.
const popoutNoticeSlot = css`
  display: flex;
  align-items: center;
  margin-left: 20px;
  min-width: 0;

  &:empty {
    display: none;
  }
`;

export const AccessOverviewPage = () => {
  const dispatch = useDispatch();

  // A bulk dialog is open in its own window: the page stays readable, but must not
  // change the results that dialog is working from.
  const lockedByPopout = usePageLockedByPopout();

  const showError = useCallback(
    (errorMsg: string) => {
      dispatch(showSnackbarAction(errorMsg, true));
    },
    [dispatch],
  );

  const clearError = (error: ApiError) =>
    dispatch(clearApiErrorAction(error.requestId, error.actionType));

  const resources = useSelector((s: IAppState) => s.locale);
  const lastFilter = useSelector((s: IAppState) => s.liftAccess?.lastFilter);
  const userId = useSelector((s: IAppState) => s.userState.userId);
  const operatorId = useSelector((s: IAppState) => s.userState.userName);

  const sellingSeasons = useSelector((s: IAppState) => s.sellingSeasons);

  const fulfillmentSeasons = useSelector(
    (s: IAppState) => s.fulfillmentSeasons,
  );

  const rawEntitlementTypes = useSelector(
    (s: IAppState) => s.entitlementTypesDetails?.allEntitlementTypes,
  );

  const rawRuleSets = useSelector(
    (s: IAppState) => s.liftAccess?.liftAccessRuleSets,
  );
  const liftAccessRuleSets = useMemo(() => rawRuleSets || [], [rawRuleSets]);
  const resortsMap = useSelector((s: IAppState) => s.resortDetails?.allResorts);
  const resortValues = useMemo(
    () => (resortsMap ? resortsMap.getValues() : []),
    [resortsMap],
  );
  const minSellingSeasonId = useSelector(
    (state: IAppState) =>
      parseInt(
        String(
          state.resortToolsConfig.globalConfigs[
            "productAdminMinSellingSeasonId"
          ],
        ),
      ) || undefined,
  );
  const minFulfillmentSeasonId = useSelector(
    (state: IAppState) =>
      parseInt(
        String(
          state.resortToolsConfig.globalConfigs[
            "productAdminMinFulfillmentSeasonId"
          ],
        ),
      ) || undefined,
  );
  const isLoadingAccessDetails = useSelector(
    (state: IAppState) =>
      !!getLoading(
        state,
        ACTION_TYPE.SEARCH_LIFT_ACCESS_RULES_REQUEST,
        ACTION_TYPE.SEARCH_LIFT_ACCESS_RULES_REQUEST,
      ),
  );
  const accessProductError = useSelector((state: IAppState) =>
    getError(
      state,
      ACTION_TYPE.SEARCH_LIFT_ACCESS_RULES_REQUEST,
      ACTION_TYPE.SEARCH_LIFT_ACCESS_RULES_REQUEST,
    ),
  );
  const resultLimitError =
    accessProductError?.extras?.status === 422
      ? getResource(
          resources,
          "ACCESS_OVERVIEW_RESULT_LIMIT_EXCEEDED",
          accessProductError.extras?.resultLimit ?? 0,
        )
      : undefined;
  const sellingSeasonsError = useSelector((state: IAppState) =>
    getError(
      state,
      ACTION_TYPE.GET_SELLING_SEASONS_REQUEST,
      ACTION_TYPE.GET_SELLING_SEASONS_REQUEST,
    ),
  );
  const fulfillmentSeasonsError = useSelector((state: IAppState) =>
    getError(
      state,
      ACTION_TYPE.GET_FULFILLMENT_SEASONS_REQUEST,
      ACTION_TYPE.GET_FULFILLMENT_SEASONS_REQUEST,
    ),
  );
  const resortDetailsError = useSelector((state: IAppState) =>
    getError(
      state,
      ACTION_TYPE.RESORT_DETAILS_REQUEST,
      ACTION_TYPE.RESORT_DETAILS_REQUEST,
    ),
  );
  const isDeleting = useSelector(
    (s: IAppState) =>
      !!getLoading(
        s,
        ACTION_TYPE.BULK_DELETE_LIFT_ACCESS_RULES_REQUEST,
        ACTION_TYPE.BULK_DELETE_LIFT_ACCESS_RULES_REQUEST,
      ),
  );
  const deleteError = useSelector((s: IAppState) =>
    getError(
      s,
      ACTION_TYPE.BULK_DELETE_LIFT_ACCESS_RULES_REQUEST,
      ACTION_TYPE.BULK_DELETE_LIFT_ACCESS_RULES_REQUEST,
    ),
  );

  const showSnackbar = useCallback(
    (msg: string) => dispatch(showSnackbarAction(msg)),
    [dispatch],
  );

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showBulkDeleteConfirmation, setShowBulkDeleteConfirmation] =
    useState(false);
  const [ruleSetToDelete, setRuleSetToDelete] =
    useState<LiftAccessRuleSet | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedSellingSeason, setSelectedSellingSeason] = useState<ItemKey[]>(
    [],
  );
  const [selectedFulfillmentSeason, setSelectedFulfillmentSeason] = useState<
    ItemKey[]
  >([]);
  const [selectedAccessNodes, setSelectedAccessNodes] = useState<ItemKey[]>([]);
  const [entitlementTypeDescriptionQuery, setEntitlementTypeDescriptionQuery] =
    useState<string>("");
  const [entitlementTypeIdQuery, setEntitlementTypeIdQuery] =
    useState<string>("");

  const isFilterValid = useMemo(() => {
    return (
      selectedSellingSeason.length > 0 ||
      selectedFulfillmentSeason.length > 0 ||
      selectedAccessNodes.length > 0 ||
      entitlementTypeIdQuery.trim() !== "" ||
      entitlementTypeDescriptionQuery.trim() !== ""
    );
  }, [
    selectedSellingSeason,
    selectedFulfillmentSeason,
    selectedAccessNodes,
    entitlementTypeIdQuery,
    entitlementTypeDescriptionQuery,
  ]);

  const showResultLimitError = resultLimitError && isFilterValid;
  const noDataMessage = showResultLimitError
    ? "" // ErrorView shows the result limit error
    : hasSearched && isFilterValid
      ? getResource(resources, "ACCESS_OVERVIEW_NO_ACCESS_DATA")
      : isFilterValid
        ? getResource(resources, "ACCESS_OVERVIEW_CLICK_SEARCH")
        : "";

  /** Paging */
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [pagedRules, setPagedRules] = useState<LiftAccessRuleSet[]>([]);

  /** Row selection, keyed by rule set identity so it survives paging and sorting. */
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const selectAllRef = useRef<HTMLInputElement | null>(null);

  /** Validated Entitlement Type IDs from by IdListSearchDropdown */
  const [validEntitlementTypeIds, setValidEntitlementTypeIds] = useState<
    number[]
  >([]);

  const sellingSeasonsSorted = useMemo(() => {
    const meetsMinId = (s: SellingSeason) =>
      minSellingSeasonId === undefined
        ? true
        : Number(s.sellingSeasonId) >= minSellingSeasonId;

    return [...(sellingSeasons ?? [])]
      .filter(meetsMinId)
      .sort(
        (a, b) =>
          Number(b.sellingSeasonId ?? (b as any).id) -
          Number(a.sellingSeasonId ?? (a as any).id),
      );
  }, [sellingSeasons, minSellingSeasonId]);

  const entitlementTypes = useMemo(() => {
    if (!rawEntitlementTypes) return [];
    const sellingSeasons =
      new Set(sellingSeasonsSorted.map((s) => s.seasonDescription)) ?? [];
    return rawEntitlementTypes
      .getValues()
      .filter((et) => et.entitlementCategoryId === ENTITLEMENT_CATEGORY_ID.LIFT)
      .filter((et) => sellingSeasons.has(et.seasonDescription));
  }, [sellingSeasonsSorted, rawEntitlementTypes]);

  const fulfillmentSeasonsSorted = useMemo(() => {
    const startsWithDisplay = (text?: string) =>
      /^display\s*/i.test((text ?? "").trim());

    const meetsMinId = (s: FulfillmentSeason) =>
      minFulfillmentSeasonId === undefined
        ? true
        : Number(s.fulfillmentSeasonId) >= minFulfillmentSeasonId;

    return [...(fulfillmentSeasons ?? [])]
      .filter((s) => !startsWithDisplay(s.seasonDescription))
      .filter(meetsMinId)
      .sort(
        (a, b) => Number(b.fulfillmentSeasonId) - Number(a.fulfillmentSeasonId),
      );
  }, [fulfillmentSeasons, minFulfillmentSeasonId]);

  const accessNodes = useMemo<AccessNode[]>(() => {
    if (!resortValues) return [];

    return resortValues
      .filter((r) => shouldIncludeResort(r, RESORT_EXCLUSIONS.ACCESS_OVERVIEW))
      .map(resortToAccessNode)
      .sort(compareByNameAsc);
  }, [resortValues]);

  // The rows the user is actually looking at. The table keeps its own sorted copy of
  // the data, so `pagedRules` is only ever the unsorted slice — Shift-click ranges
  // must be measured against this or they cover the wrong rows once a sort is applied.
  const displayedRulesRef = useRef<LiftAccessRuleSet[]>([]);

  const { toggle: toggleRowSelection, clearAnchor: clearSelectionAnchor } =
    useRowRangeSelection<string>({
      setSelectedKeys,
      getOrderedKeys: useCallback(
        () =>
          (displayedRulesRef.current.length
            ? displayedRulesRef.current
            : pagedRules
          ).map(liftAccessRuleSetKey),
        [pagedRules],
      ),
    });

  // Kept stable so the table's column config stays referentially equal between
  // renders: it compares columns by value, and a fresh callback here makes every
  // parent render look like a column change and re-sort the whole result set.
  const sortAccessRules = useCallback(
    (
      property: keyof LiftAccessRuleSet,
      asc: boolean,
      useConcatenatedString: boolean = false,
      ruleProperty?: string,
    ) => {
      const value = liftAccessRuleSets[0]?.[property];

      let sortedRules;

      // Array of rule objects case
      if (Array.isArray(value) && ruleProperty) {
        sortedRules = sortArrayOfObjectsProperty(
          property,
          ruleProperty,
          asc,
          liftAccessRuleSets,
          useConcatenatedString,
        );
      }

      // Fallback: (string/number/date)
      else {
        sortedRules = sortByProp(property, false, asc, liftAccessRuleSets);
      }

      const displayed = sortedRules.slice(
        currentPage * rowsPerPage,
        (currentPage + 1) * rowsPerPage,
      );

      displayedRulesRef.current = displayed;
      return displayed;
    },
    [liftAccessRuleSets, currentPage, rowsPerPage],
  );

  const buildQuery = useCallback((): AccessOverviewQuery => {
    const sellingSeasonIds = extractIds(selectedSellingSeason);
    const fulfillmentSeasonIds = extractIds(selectedFulfillmentSeason);
    const resortIds = extractIds(selectedAccessNodes); // selectedAccessNodes are in format "resort:id"
    const description =
      (entitlementTypeDescriptionQuery ?? "").trim() || undefined;
    const entitlementTypeIds =
      validEntitlementTypeIds && validEntitlementTypeIds.length > 0
        ? validEntitlementTypeIds
        : undefined;

    const query: AccessOverviewQuery = {
      sellingSeasonIds: sellingSeasonIds.length ? sellingSeasonIds : undefined,
      fulfillmentSeasonIds: fulfillmentSeasonIds.length
        ? fulfillmentSeasonIds
        : undefined,
      resortIds: resortIds.length ? resortIds : undefined,
      entitlementTypeIds,
      entitlementDescriptionFilter: description,
    };

    return query;
  }, [
    selectedSellingSeason,
    selectedFulfillmentSeason,
    selectedAccessNodes,
    entitlementTypeDescriptionQuery,
    validEntitlementTypeIds,
  ]);

  /**
   * Runs the current filter. A refresh keeps the selection — it is keyed by rule set
   * identity, so surviving rows stay selected and gone ones drop out. A new search
   * does not.
   */
  const runSearch = useCallback(
    ({ preserveSelection }: { preserveSelection: boolean }) => {
      const query = buildQuery();

      if (!isFilterValid) {
        showError(getResource(resources, "ACCESS_OVERVIEW_SET_FILTERS"));
        return;
      }

      // Clear existing *non‑422* errors BEFORE dispatch
      if (accessProductError && accessProductError?.extras?.status !== 422) {
        dispatch(
          clearApiErrorAction(
            ACTION_TYPE.SEARCH_LIFT_ACCESS_RULES_REQUEST,
            ACTION_TYPE.SEARCH_LIFT_ACCESS_RULES_REQUEST,
          ),
        );
      }

      setHasSearched(true);
      setCurrentPage(0);
      setPagedRules([]);

      if (!preserveSelection) {
        // A new result set means the previous selection no longer refers to
        // anything the user can see, so it goes with it.
        setSelectedKeys(new Set());
      }

      // Dropped either way: the anchor points at a row index, which a new result
      // set invalidates even when the keys survive.
      clearSelectionAnchor();

      dispatch(searchLiftAccessRulesAction(query));
    },
    [
      buildQuery,
      isFilterValid,
      accessProductError,
      resources,
      dispatch,
      showError,
      clearSelectionAnchor,
    ],
  );

  /** Takes no arguments: wired to onSearch, so a click event would land in param one. */
  const handleSearch = useCallback(() => {
    runSearch({ preserveSelection: false });
  }, [runSearch]);

  /** Re-reads the current filter without disturbing what the user has selected. */
  const refreshResults = useCallback(() => {
    runSearch({ preserveSelection: true });
  }, [runSearch]);

  const handleRowsPerPageSelection = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(0);
  };

  useEffect(() => {
    if (!sellingSeasons?.length) {
      dispatch(getSellingSeasonsAction());
    }

    if (!fulfillmentSeasons?.length) {
      dispatch(getFulfillmentSeasonsAction());
    }

    if (!resortsMap || resortsMap.isEmpty()) {
      dispatch(getResortDetailsAction());
    }
  }, [
    sellingSeasons?.length,
    fulfillmentSeasons?.length,
    resortsMap,
    dispatch,
  ]);

  useEffect(() => {
    if (lastFilter) {
      const lastQuery = denormalizeFilter(lastFilter);
      if (lastQuery) {
        setSelectedSellingSeason(
          lastQuery.sellingSeasonIds
            ? lastQuery.sellingSeasonIds.map(String)
            : [],
        );

        setSelectedFulfillmentSeason(
          lastQuery.fulfillmentSeasonIds
            ? lastQuery.fulfillmentSeasonIds.map(String)
            : [],
        );

        setSelectedAccessNodes(
          lastQuery.resortIds
            ? lastQuery.resortIds.map((id) => `resort:${id}`)
            : [],
        );

        setEntitlementTypeIdQuery(
          lastQuery.entitlementTypeIds?.join(",") || "",
        );

        setEntitlementTypeDescriptionQuery(
          lastQuery.entitlementDescriptionFilter || "",
        );
      }
    }
  }, [dispatch, lastFilter]);

  useEffect(() => {
    const start = currentPage * rowsPerPage;
    const end = start + rowsPerPage;
    setPagedRules(liftAccessRuleSets.slice(start, end));
  }, [liftAccessRuleSets, currentPage, rowsPerPage]);

  useEffect(() => {
    setHasSearched(false);
  }, [
    selectedSellingSeason,
    selectedFulfillmentSeason,
    selectedAccessNodes,
    entitlementTypeIdQuery,
    entitlementTypeDescriptionQuery,
  ]);

  useApiErrorSnackbar(
    [
      {
        error: sellingSeasonsError,
        clear: clearError,
        resourceKey: "ACCESS_OVERVIEW_LOADING_SELLING_SEASONS_ERROR",
      },
      {
        error: fulfillmentSeasonsError,
        clear: clearError,
        resourceKey: "ACCESS_OVERVIEW_LOADING_FULFILLMENT_SEASONS_ERROR",
      },
      {
        error: resortDetailsError,
        clear: clearError,
        resourceKey: "ACCESS_OVERVIEW_LOADING_RESORT_DETAILS_ERROR",
      },
      {
        error: deleteError,
        clear: clearError,
        resourceKey: "ACCESS_RULE_DELETE_ERROR",
      },

      {
        // Only send non‑422-result-limit errors to Snackbar
        error: accessProductError?.extras?.isResultLimitError
          ? undefined
          : accessProductError,
        clear: clearError,
        resourceKey: "ACCESS_OVERVIEW_LOADING_ACCESS_PRODUCTS_ERROR",
      },
    ],
    showError,
  );

  // Single rule set delete only — the bulk run is owned by the progress card below
  // and must not arm this, or the shared loading flag would resolve it mid-run.
  const [ownsDelete, setOwnsDelete] = useState(false);

  const completeDelete = useCallback(() => {
    showSnackbar(getResource(resources, "ACCESS_RULE_DELETE_SUCCESS"));
    setShowDeleteConfirmation(false);
    setOwnsDelete(false);
  }, [showSnackbar, resources]);

  // Without this `ownsDelete` stays true after a failure, leaving the handler armed
  // for the next resolution. The error itself comes from the deleteError snackbar.
  const failDelete = useCallback(() => {
    setOwnsDelete(false);
  }, []);

  const handleEditRuleSet = (ruleSet: LiftAccessRuleSet) => {
    dispatch(
      showModalAction(IKON_MODAL.ACCESS_INDIVIDUAL_EDIT, {
        ruleSet: ruleSet,
        onCommit: refreshSearchResultsAfterEdit,
        onDelete: completeDelete,
      }),
    );
  };

  const handleDeleteRuleSet = () => {
    if (!ruleSetToDelete) return;

    const deleteRequest = buildSingleRuleSetDeleteRequest({
      userId: userId,
      operatorId: operatorId,
      ruleSet: ruleSetToDelete,
    });

    setOwnsDelete(true);
    dispatch(bulkDeleteLiftAccessRuleSetsAction(deleteRequest));
    setShowDeleteConfirmation(false);
    setRuleSetToDelete(null);
  };

  useApiActionHandler({
    isPending: isDeleting,
    error: deleteError,
    enabled: ownsDelete,
    onSuccess: completeDelete,
    onError: failDelete,
  });

  const refreshSearchResultsAfterEdit = useCallback(() => {
    // TODO:  Consider whether to reload only the changed ESFL
    handleSearch();
  }, [handleSearch]);

  const saveProgress = useSelector((s: IAppState) => s.bulkOperationProgress);
  const [isSaveProgressVisible, setIsSaveProgressVisible] = useState(false);
  const [saveRun, setSaveRun] = useState<BulkSaveRun | null>(null);
  const [showSaveFailures, setShowSaveFailures] = useState(false);
  const [isSaveRunning, setIsSaveRunning] = useState(false);
  const [isSaveCancelling, setIsSaveCancelling] = useState(false);
  const [saveRunCancelled, setSaveRunCancelled] = useState(false);
  // By reference, so the running loop sees a cancel without being restarted.
  const saveCancelRef = useRef({ cancelled: false });
  // Which run the shared progress card is reporting on: they differ in wording, and
  // in what closing the card does to the selection.
  const [progressKind, setProgressKind] = useState<"save" | "delete">("save");

  /** Bulk delete run */
  const [deleteRun, setDeleteRun] = useState<BulkDeleteRun | null>(null);
  const [isDeleteRunning, setIsDeleteRunning] = useState(false);
  const [isDeleteCancelling, setIsDeleteCancelling] = useState(false);
  // Held while the retry confirmation is open, so the gate acts on the same rule sets
  // the count named.
  const [retryDeleteRuleSets, setRetryDeleteRuleSets] = useState<
    LiftAccessRuleSet[] | null
  >(null);
  // By reference, so the running loop sees a cancel without being restarted.
  const deleteCancelRef = useRef({ cancelled: false });

  // Minutes of irreversible work, and the page holds the only record of how far it
  // got — leaving mid-run should at least be deliberate. Raised only while batches
  // are in flight, and dropped as soon as the run settles, fails, or is refused.
  useEffect(() => {
    if (!isDeleteRunning) return;

    const warnBeforeLeaving = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };

    window.addEventListener("beforeunload", warnBeforeLeaving);
    return () => window.removeEventListener("beforeunload", warnBeforeLeaving);
  }, [isDeleteRunning]);

  /** Search result freshness — bounds how old a snapshot a bulk edit may write back. */
  const { resultsStale } = useSearchResultFreshness();

  /** Selection */
  // At most one entry per identity: a duplicate row in the results would otherwise
  // be counted twice, and the count is what the bulk actions report on.
  const selectedRuleSets = useMemo(() => {
    const taken = new Set<string>();
    return liftAccessRuleSets.filter((rs) => {
      const key = liftAccessRuleSetKey(rs);
      if (!selectedKeys.has(key) || taken.has(key)) return false;
      taken.add(key);
      return true;
    });
  }, [liftAccessRuleSets, selectedKeys]);

  const selectedCount = selectedRuleSets.length;
  const allSelected =
    liftAccessRuleSets.length > 0 &&
    selectedCount === liftAccessRuleSets.length;

  const handleToggleSelect = useCallback(
    (ruleSet: LiftAccessRuleSet, selected: boolean, shiftKey: boolean) =>
      toggleRowSelection(liftAccessRuleSetKey(ruleSet), selected, shiftKey),
    [toggleRowSelection],
  );

  // Selects every rule set the search returned, not just the current page.
  const handleToggleSelectAll = useCallback(
    (checked: boolean) => {
      setSelectedKeys(
        checked
          ? new Set(liftAccessRuleSets.map(liftAccessRuleSetKey))
          : new Set(),
      );
    },
    [liftAccessRuleSets],
  );

  const clearSelection = useCallback(() => {
    setSelectedKeys(new Set());
    clearSelectionAnchor();
  }, [clearSelectionAnchor]);

  /** Bulk edit run */
  // Shared by the first attempt and Retry, so both report through the same progress
  // card and can both land on the failures screen.
  const executeBulkSave = useCallback(
    async (run: BulkSaveRun, requests: AccessRuleChangeRequest[]) => {
      dispatch({ type: ACTION_TYPE.BULK_ADD_LIFT_ACCESS_RULES_BATCH_RESET });
      setShowSaveFailures(false);
      setProgressKind("save");
      setIsSaveProgressVisible(true);

      // A fresh ref per run: a flag left set by the last cancel would stop this one
      // before its first request.
      saveCancelRef.current = { cancelled: false };
      setIsSaveCancelling(false);
      setSaveRunCancelled(false);
      setIsSaveRunning(true);

      let result: LiftAccessRuleBatchSaveResult | undefined;
      try {
        result = await dispatch(
          bulkSaveLiftAccessRulesBatched(requests, {
            cancelRef: saveCancelRef.current,
          }) as any,
        );
      } catch (e) {
        // The runner settles the card itself; if it dies before doing so, the card
        // is left with no Close button over a full-screen overlay.
        console.error("Bulk save run threw", e);
        dispatch({
          type: ACTION_TYPE.BULK_ADD_LIFT_ACCESS_RULES_BATCH_COMPLETE,
          payload: {},
        });
      } finally {
        setIsSaveRunning(false);
      }

      setSaveRunCancelled(!!result?.cancelled);

      const outcomes = mergeSaveOutcomes(run.outcomes, result);
      const settled: BulkSaveRun = {
        ...run,
        outcomes,
        notAttempted: notAttemptedCount(run.requests, outcomes),
      };
      setSaveRun(settled);

      // A clean save — or a retry that cleared everything — keeps the progress card,
      // which reports the success itself.
      if ([...outcomes.values()].some((outcome) => !outcome.success)) {
        setShowSaveFailures(true);
      }
    },
    [dispatch],
  );

  // Owned here rather than by the dialog, which closes as it hands the requests
  // over: a dialog left open behind the progress card can fire a second save.
  const runBulkEdit = useCallback(
    async (saveRequests: AccessRuleChangeRequest[]) => {
      if (saveRequests.length === 0) return;

      // Captured now: the failures screen outlives the selection, which Close clears.
      const identities = new Map<string, BulkFailureIdentity>(
        selectedRuleSets.map((ruleSet) => [
          liftAccessRuleSetKey(ruleSet),
          ruleSet,
        ]),
      );

      await executeBulkSave(
        {
          requests: saveRequests,
          identities,
          outcomes: new Map(),
          notAttempted: 0,
        },
        saveRequests,
      );
    },
    [executeBulkSave, selectedRuleSets],
  );

  // Checked between waves: requests already dispatched finish, and their saves stand.
  const cancelBulkSave = useCallback(() => {
    saveCancelRef.current.cancelled = true;
    setIsSaveCancelling(true);
  }, []);

  // Only the transient failures; deterministic ones would fail identically.
  const retryFailedSaves = useCallback(() => {
    if (!saveRun) return;
    const requests = retryableSaveRequests(saveRun);
    if (requests.length === 0) return;
    void executeBulkSave(saveRun, requests);
  }, [saveRun, executeBulkSave]);

  const closeSaveProgress = useCallback(() => {
    dispatch({ type: ACTION_TYPE.BULK_ADD_LIFT_ACCESS_RULES_BATCH_RESET });
    setIsSaveProgressVisible(false);
    setShowSaveFailures(false);
    setSaveRun(null);
    setIsSaveCancelling(false);
    setSaveRunCancelled(false);
    setDeleteRun(null);
    setIsDeleteCancelling(false);
    setRetryDeleteRuleSets(null);
    // Refreshing is what puts the saved values into the grid. Until it lands the
    // rows still carry pre-save rules, and the selection is stale by definition —
    // re-applying an "add" against it would write the rules a second time.
    //
    // A delete run has already re-scoped the selection to the rule sets it did not
    // delete, so clearing here would throw away the only handle on a retry. The
    // re-search still runs: it corrects for rows another session changed during the
    // run, and for chunks whose responses never arrived.
    if (progressKind !== "delete") clearSelection();
    refreshSearchResultsAfterEdit();
  }, [dispatch, progressKind, clearSelection, refreshSearchResultsAfterEdit]);

  const handleBulkEditRuleSets = useCallback(() => {
    if (selectedRuleSets.length === 0) return;

    dispatch(
      showModalAction(IKON_MODAL.ACCESS_MULTI_EDIT, {
        ruleSets: selectedRuleSets,
        onApply: runBulkEdit,
      }),
    );
  }, [dispatch, selectedRuleSets, runBulkEdit]);

  const openBulkDeleteModal = useCallback(() => {
    // The published per-run limit is checked here, before anything is sent, rather
    // than per chunk — over the limit nothing is deleted at all.
    if (
      selectedRuleSets.length === 0 ||
      selectedRuleSets.length > MAX_BULK_DELETE_SELECTION
    ) {
      return;
    }
    setShowBulkDeleteConfirmation(true);
  }, [selectedRuleSets]);

  const closeBulkDeleteModal = useCallback(
    () => setShowBulkDeleteConfirmation(false),
    [],
  );

  // Shared by the first attempt and Retry, so both report through the same progress
  // card and can both land on the outcome screen.
  const executeBulkDelete = useCallback(
    async (run: BulkDeleteRun, attempt: LiftAccessRuleSet[]) => {
      dispatch({ type: ACTION_TYPE.BULK_ADD_LIFT_ACCESS_RULES_BATCH_RESET });
      setShowSaveFailures(false);
      setProgressKind("delete");
      setIsSaveProgressVisible(true);

      // A fresh ref per run: a flag left set by the last cancel would stop this one
      // before its first batch.
      deleteCancelRef.current = { cancelled: false };
      setIsDeleteCancelling(false);
      setIsDeleteRunning(true);

      let result: LiftAccessRuleBatchSaveResult | undefined;
      try {
        result = await dispatch(
          bulkDeleteLiftAccessRulesChunked(attempt, {
            userId,
            operatorId,
            cancelRef: deleteCancelRef.current,
          }) as any,
        );
      } catch (e) {
        // The runner settles the card itself; if it dies before doing so, the card is
        // left with no Close button over a full-screen overlay.
        console.error("Bulk delete run threw", e);
        dispatch({
          type: ACTION_TYPE.BULK_ADD_LIFT_ACCESS_RULES_BATCH_COMPLETE,
          payload: {},
        });
      } finally {
        setIsDeleteRunning(false);
      }

      // Merged, not replaced: a retry only reports on what it re-sent, so replacing
      // would make everything deleted on the first attempt look unattempted.
      const outcomes = mergeSaveOutcomes(run.outcomes, result);
      const settled: BulkDeleteRun = {
        ...run,
        outcomes,
        cancelled: !!result?.cancelled,
      };
      setDeleteRun(settled);

      // Keyed on what the response reported deleted, never on what was requested: a
      // chunk that failed, or one the run never reached, leaves its rule sets both
      // listed and selected, so a second attempt acts on exactly those.
      const deletedKeys = new Set(
        [...outcomes.entries()]
          .filter(([, outcome]) => outcome.success)
          .map(([key]) => key),
      );
      setSelectedKeys(
        new Set(
          run.ruleSets
            .map(liftAccessRuleSetKey)
            .filter((key) => !deletedKeys.has(key)),
        ),
      );

      // Failures get the outcome screen, which names them. A run stopped with none —
      // cancelled cleanly — stays on the progress card, which reports the stop itself.
      if (deleteFailureCount(settled) > 0) {
        setShowSaveFailures(true);
        return;
      }

      if (notAttemptedRuleSets(settled).length === 0 && !result?.stoppedReason) {
        showSnackbar(
          getResource(
            resources,
            "ACCESS_RULE_DELETE_SUCCESS_COUNT",
            deleteSuccessCount(settled),
          ),
        );
      }
    },
    [dispatch, userId, operatorId, showSnackbar, resources],
  );

  // Runs the delete in bounded chunks; the modal has already passed the type-to-confirm
  // gate. Owned here rather than by the dialog, which closes as the run starts.
  const handleBulkDeleteRuleSets = useCallback(() => {
    if (
      selectedRuleSets.length === 0 ||
      selectedRuleSets.length > MAX_BULK_DELETE_SELECTION ||
      isSaveProgressVisible
    ) {
      return;
    }

    // Snapshotted before the first chunk: the reducer prunes deleted rows as chunks
    // land, which shrinks selectedRuleSets under the run.
    const runRuleSets = selectedRuleSets;

    setShowBulkDeleteConfirmation(false);
    void executeBulkDelete(
      { ruleSets: runRuleSets, outcomes: new Map(), cancelled: false },
      runRuleSets,
    );
  }, [selectedRuleSets, isSaveProgressVisible, executeBulkDelete]);

  // Checked between batches: a request already sent finishes, and its deletions stand.
  const cancelBulkDelete = useCallback(() => {
    deleteCancelRef.current.cancelled = true;
    setIsDeleteCancelling(true);
  }, []);

  // Retry is the same irreversible bulk delete, so it goes back through the same
  // typed-confirmation gate rather than firing on one click.
  const openRetryDeleteConfirm = useCallback(() => {
    if (!deleteRun) return;
    const retryable = retryableDeleteRuleSets(deleteRun);
    if (retryable.length === 0) return;
    setRetryDeleteRuleSets(retryable);
  }, [deleteRun]);

  const confirmRetryDelete = useCallback(() => {
    const retryable = retryDeleteRuleSets;
    setRetryDeleteRuleSets(null);
    if (!deleteRun || !retryable?.length) return;
    void executeBulkDelete(deleteRun, retryable);
  }, [deleteRun, retryDeleteRuleSets, executeBulkDelete]);

  const handleAddRuleSets = () => {
    dispatch(
      showModalAction(IKON_MODAL.ACCESS_MULTI_CREATE, {
        sellingSeasons: sellingSeasonsSorted,
        fulfillmentSeasons: fulfillmentSeasonsSorted,
        entitlementTypes: entitlementTypes,
        resorts: resortValues,
        onCommit: (query: AccessOverviewQuery) => {
          refreshSearchResultsAfterAdd(query);
        },
      }),
    );
  };

  const refreshSearchResultsAfterAdd = useCallback(
    (query: AccessOverviewQuery) => {
      setSelectedSellingSeason(query.sellingSeasonIds?.map(String) ?? []);
      setSelectedFulfillmentSeason(
        query.fulfillmentSeasonIds?.map(String) ?? [],
      );
      setSelectedAccessNodes(
        query.resortIds?.map((id) => `resort:${id}`) ?? [],
      );
      setEntitlementTypeIdQuery(query.entitlementTypeIds?.join(",") ?? "");
      dispatch(searchLiftAccessRulesAction(query));
      setHasSearched(true);
    },
    [dispatch],
  );

  const closeDeleteModal = () => {
    setShowDeleteConfirmation(false);
    setRuleSetToDelete(null);
  };

  const buildDeleteDetails = (ruleSet: LiftAccessRuleSet | null) => {
    if (!ruleSet) return null;

    return (
      <>
        <div
          css={css`
            margin-bottom: 15px;
          `}
        >
          {getResource(resources, "ACCESS_RULE_SET_DELETE_CONFIRMATION")}
        </div>
        <div
          css={css`
            transform: scale(0.95);
            background: ${LIGHT_GREY};
            margin-bottom: 15px;
            max-width: 900px;
          `}
        >
          <LiftAccessRuleSetHeader ruleSet={ruleSet} resources={resources} />
        </div>
        <div>{getResource(resources, "ACCESS_RULE_DELETE_QUESTION")}</div>
      </>
    );
  };

  return (
    <>
      <Column rootCss={accessOverviewRoot}>
        <Row alignItems="center">
          <div
            inert={lockedByPopout}
            css={lockedByPopout ? lockedRegion : undefined}
          >
            <Row>
              <AccessOverviewFilters
                resources={resources}
                sellingSeasons={sellingSeasonsSorted}
                fulfillmentSeasons={fulfillmentSeasonsSorted}
                accessNodes={accessNodes}
                selectedSellingSeason={selectedSellingSeason}
                selectedFulfillmentSeason={selectedFulfillmentSeason}
                selectedAccessNodes={selectedAccessNodes}
                entitlementTypeIdQuery={entitlementTypeIdQuery}
                entitlementTypeDescriptionQuery={
                  entitlementTypeDescriptionQuery
                }
                onSellingSeasonChange={setSelectedSellingSeason}
                onFulfillmentSeasonChange={setSelectedFulfillmentSeason}
                onAccessNodesChange={setSelectedAccessNodes}
                onIdQueryChange={setEntitlementTypeIdQuery}
                onDescriptionQueryChange={setEntitlementTypeDescriptionQuery}
                onValidIdListChange={setValidEntitlementTypeIds}
                onSearch={handleSearch}
              />
              <Buttons.MediumNeutral
                css={addButton}
                type="button"
                id="add-button"
                title={getResource(resources, "ACCESS_OVERVIEW_ADD_TITLE")}
                onClick={handleAddRuleSets}
              >
                {getResource(resources, "ACCESS_OVERVIEW_ADD")}
              </Buttons.MediumNeutral>
            </Row>
          </div>
          <div id={POPOUT_NOTICE_SLOT_ID} css={popoutNoticeSlot} />
        </Row>
        <Column rootCss={tableSection}>
          {liftAccessRuleSets.length > 0 && (
            <Row
              rootCss={
                lockedByPopout
                  ? css`
                      ${selectionRow};
                      ${lockedRegion};
                    `
                  : selectionRow
              }
              // Select-all and the bulk actions would act behind the detached
              // dialog's back.
              inert={lockedByPopout}
            >
              <SelectAllToolbar
                selectAllRef={selectAllRef}
                allSelected={allSelected}
                selectedCount={selectedCount}
                shownCount={liftAccessRuleSets.length}
                isFiltered={false}
                // Names the scope: the table is paged, but this selects every
                // result — including rows on pages not currently rendered.
                selectAllLabel={getResource(
                  resources,
                  "ACCESS_RULE_SELECT_ALL_RESULTS",
                  liftAccessRuleSets.length,
                )}
                selectAllTitle={getResource(
                  resources,
                  "ACCESS_RULE_SELECT_ALL_RESULTS_TITLE",
                )}
                clearTitle={getResource(
                  resources,
                  "ACCESS_RULE_CLEAR_SELECTION_TITLE",
                  selectedCount,
                )}
                onToggle={handleToggleSelectAll}
                onClear={clearSelection}
                clearLabel={getResource(
                  resources,
                  "ACCESS_RULE_CLEAR_SELECTION",
                )}
              />

              {selectedCount > 0 && (
                <div css={bulkActions}>
                  {/* Disabled while results reload: the rows still show pre-save
                      rules, and an edit composed against them can duplicate. */}
                  <Buttons.SmallNeutral
                    type="button"
                    title={getResource(
                      resources,
                      resultsStale
                        ? "ACCESS_RULE_BULK_EDIT_RESULTS_STALE"
                        : "ACCESS_RULE_EDIT_SELECTED_TITLE",
                    )}
                    onClick={handleBulkEditRuleSets}
                    disabled={isLoadingAccessDetails || resultsStale}
                  >
                    {getResource(resources, "ACCESS_RULE_EDIT_SELECTED")}
                  </Buttons.SmallNeutral>
                  <Buttons.SmallSecondary
                    type="button"
                    title={getResource(
                      resources,
                      isDeleting || isSaveProgressVisible
                        ? "ACCESS_RULE_BULK_DELETE_PENDING_TITLE"
                        : resultsStale
                          ? "ACCESS_RULE_BULK_EDIT_RESULTS_STALE"
                          : selectedCount > MAX_BULK_DELETE_SELECTION
                            ? "ACCESS_RULE_BULK_DELETE_TOO_MANY_TITLE"
                            : "ACCESS_RULE_DELETE_SELECTED_TITLE",
                      MAX_BULK_DELETE_SELECTION,
                    )}
                    onClick={openBulkDeleteModal}
                    disabled={
                      isLoadingAccessDetails ||
                      isDeleting ||
                      isSaveProgressVisible ||
                      selectedCount > MAX_BULK_DELETE_SELECTION ||
                      // Deletes everything for a context at execution time, so a stale
                      // grid can show three rules and remove five.
                      resultsStale
                    }
                  >
                    {getResource(resources, "ACCESS_RULE_DELETE_SELECTED")}
                  </Buttons.SmallSecondary>
                  {/* The button titles only show on hover, and disabled buttons with
                      no stated reason read as broken. Clicking this fixes it. */}
                  {resultsStale && (
                    <button
                      type="button"
                      css={staleResultsNoticeCss}
                      onClick={refreshResults}
                      disabled={isLoadingAccessDetails}
                      title={getResource(
                        resources,
                        "ACCESS_RULE_BULK_EDIT_RESULTS_STALE",
                      )}
                    >
                      <img src={failureIcon} alt="" aria-hidden="true" />
                      {getResource(
                        resources,
                        "ACCESS_RULE_BULK_EDIT_RESULTS_STALE_SHORT",
                      )}
                    </button>
                  )}
                </div>
              )}
            </Row>
          )}
          <Row
            rootCss={css`
              margin-left: 25px;
              margin-top: 5px;
              display: flex;
              flex-direction: row;
            `}
          >
            <ErrorView
              error={
                (showResultLimitError && resultLimitError) ||
                (!isFilterValid
                  ? getResource(resources, "ACCESS_OVERVIEW_SET_FILTERS")
                  : undefined)
              }
              boxStyle={BoxedErrorInverseNoBorder}
            />

            {/* Pagination Bar */}
            <div css={tablePagination}>
              <TablePagination
                component="div"
                count={liftAccessRuleSets.length}
                page={currentPage}
                onPageChange={(event, newPage) => setCurrentPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  handleRowsPerPageSelection(e);
                }}
                labelRowsPerPage={getResource(
                  resources,
                  "ACCESS_OVERVIEW_ROWS_PER_PAGE",
                )}
                rowsPerPageOptions={[50, 75, 100]}
                showFirstButton
                showLastButton
              />
            </div>
          </Row>

          {/* Access Rules Table */}
          <div css={tableContainer}>
            <LiftAccessRuleSetTable
              rules={pagedRules}
              isLoading={isLoadingAccessDetails}
              noDataMessage={noDataMessage}
              onSortRules={sortAccessRules}
              rootCss={tableRoot}
              selectable
              selectedKeys={selectedKeys}
              onToggleSelect={handleToggleSelect}
              // Rows stay readable, scrollable and sortable; only selection and
              // the row menu are off.
              locked={lockedByPopout}
              onEditRuleSet={handleEditRuleSet}
              onDeleteRuleSet={(ruleSet) => {
                setRuleSetToDelete(ruleSet);
                setShowDeleteConfirmation(true);
              }}
            />
          </div>
        </Column>
      </Column>

      {showDeleteConfirmation && (
        <ConfirmationModal
          resources={resources}
          icon={deleteIcon}
          style={BoxedErrorInverse}
          title={getResource(resources, "ACCESS_RULE_DELETE_ACCESS_RULES")}
          styledMessage={getResource(
            resources,
            "ACCESS_RULE_DATA_CHANGE_WARNING",
          )}
          message={buildDeleteDetails(ruleSetToDelete)}
          positiveButtonText={getResource(resources, "ACCESS_RULE_DELETE")}
          negativeButtonText={getResource(
            resources,
            "ACCESS_RULE_MODAL_CANCEL",
          )}
          onPositiveButtonClick={handleDeleteRuleSet}
          onNegativeButtonClick={closeDeleteModal}
          onDismiss={closeDeleteModal}
        />
      )}

      {showBulkDeleteConfirmation && (
        <AccessMultiDeleteModal
          ruleSets={selectedRuleSets}
          onConfirm={handleBulkDeleteRuleSets}
          onDismiss={closeBulkDeleteModal}
        />
      )}

      {/* Fixed full-screen overlay, so it also blocks the page while the run is live.
          Yields to the failures screen. */}
      <BulkProgressCard
        resources={resources}
        visible={isSaveProgressVisible && !!saveProgress && !showSaveFailures}
        current={saveProgress?.current ?? 0}
        total={saveProgress?.total ?? 1}
        successCount={saveProgress?.successCount ?? 0}
        failureCount={saveProgress?.failureCount ?? 0}
        title={getResource(
          resources,
          progressKind === "delete"
            ? "ACCESS_RULE_BULK_DELETE_DELETING"
            : "ACCESS_RULE_BULK_EDIT_APPLYING",
        )}
        itemTitle={getResource(resources, "ACCESS_RULE_RULE_SETS")}
        errorMessage={getResource(
          resources,
          progressKind === "delete"
            ? "ACCESS_RULE_BULK_DELETE_ERROR_SUMMARY"
            : "ACCESS_RULE_BULK_EDIT_SAVE_ERROR",
        )}
        // From the slice, not "processed everything": a run that stops early never
        // reaches its total and would otherwise leave no way to close the card.
        isComplete={saveProgress?.isComplete}
        showCancel={
          progressKind === "delete" ? isDeleteRunning : isSaveRunning
        }
        isCancelling={
          progressKind === "delete" ? isDeleteCancelling : isSaveCancelling
        }
        isCancelled={saveProgress?.isCancelled}
        cancelledTitle={
          progressKind === "delete"
            ? getResource(resources, "ACCESS_RULE_BULK_DELETE_STOPPED_TITLE")
            : undefined
        }
        // Says what was deleted before it stopped, as prominently as what wasn't:
        // "Cancelled" alone reads as "nothing happened", which for a delete is the
        // most dangerous possible misreading.
        cancelledMessage={
          progressKind === "delete" && deleteRun
            ? getResource(
                resources,
                "ACCESS_RULE_BULK_DELETE_CANCELLED_MESSAGE",
                deleteSuccessCount(deleteRun),
                notAttemptedRuleSets(deleteRun).length,
              )
            : undefined
        }
        cancellingHint={
          progressKind === "delete"
            ? getResource(resources, "ACCESS_RULE_BULK_DELETE_CANCELLING_HINT")
            : undefined
        }
        onCancel={progressKind === "delete" ? cancelBulkDelete : cancelBulkSave}
        onClose={closeSaveProgress}
      />

      {/* Which rule sets didn't save, and why — a count alone doesn't say which. */}
      {showSaveFailures && saveRun && progressKind !== "delete" && (
        <BulkSaveFailuresView
          failures={toSaveFailures(saveRun)}
          successCount={successCount(saveRun)}
          failureCount={failureCount(saveRun)}
          notAttemptedCount={saveRun.notAttempted}
          retryableCount={retryableSaveRequests(saveRun).length}
          cancelled={saveRunCancelled}
          onRetry={retryFailedSaves}
          onClose={closeSaveProgress}
        />
      )}

      {/* The same screen, delete copy. Hidden while the retry gate is up, so the two
          dialogs never fight over focus. */}
      {showSaveFailures &&
        deleteRun &&
        progressKind === "delete" &&
        !retryDeleteRuleSets && (
          <BulkSaveFailuresView
            operation="delete"
            failures={toDeleteFailures(deleteRun)}
            successCount={deleteSuccessCount(deleteRun)}
            failureCount={deleteFailureCount(deleteRun)}
            notAttemptedCount={notAttemptedRuleSets(deleteRun).length}
            // The retryable failures only — not the unattempted, which stay selected
            // instead. The count names what a second delete would send.
            retryableCount={retryableDeleteRuleSets(deleteRun).length}
            cancelled={deleteRun.cancelled}
            onRetry={openRetryDeleteConfirm}
            onClose={closeSaveProgress}
          />
        )}

      {/* Retry deletes for real, so it passes the same gate as the first attempt. */}
      {retryDeleteRuleSets && (
        <AccessMultiDeleteModal
          ruleSets={retryDeleteRuleSets}
          onConfirm={confirmRetryDelete}
          onDismiss={() => setRetryDeleteRuleSets(null)}
        />
      )}
    </>
  );
};
